import type {
  BusinessOnboarding as CoreBusinessOnboarding,
  BusinessOnboardingMeta as CoreBusinessOnboardingMeta,
} from '@afriex/core/domain/types';

export {AfriexOnboardingProcessor} from '@afriex/core/domain/enums';
export type {
  BusinessAdminInviteRequest,
  BusinessOnboardingEditRequest,
} from '@afriex/core/domain/types';

export enum BusinessOnboardingStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

/**
 * Extends core's BusinessOnboardingMeta; `status` is typed with the admin-local
 * BusinessOnboardingStatus enum (core types it as a bare string union) so the
 * Invites screens can compare against the enum members.
 */
export type BusinessOnboardingMeta = Omit<
  CoreBusinessOnboardingMeta,
  'status'
> & {
  status?: BusinessOnboardingStatus;
};

/**
 * Admin-side view of a core BusinessOnboarding record as received over the wire.
 * Extends the core type (the single source of truth for the identity fields) and
 * overrides only what the JSON boundary or the admin UI changes:
 * - createdAt/updatedAt arrive as ISO strings, not Date;
 * - businessCountry / businessPhone.countryCode are raw strings (admin forms bind
 *   plain <select> values, not the AfriexCountryCode enum);
 * - meta is re-typed to the admin BusinessOnboardingMeta (enum-typed status).
 * Server-only fields (nonceExpiresAt, otp) are dropped; the list endpoint never
 * sends them.
 */
export interface BusinessOnboarding extends Omit<
  CoreBusinessOnboarding,
  | 'createdAt'
  | 'updatedAt'
  | 'businessPhone'
  | 'businessCountry'
  | 'meta'
  | 'nonceExpiresAt'
  | 'otp'
> {
  createdAt: string;
  updatedAt: string;
  businessPhone?: {value: string; countryCode: string};
  businessCountry?: string;
  meta?: BusinessOnboardingMeta;
}
